/**
 * `<caos-work-tabs>` — the strip of OPEN WORK across the top of a developer-tools destination.
 * (Catalog: Navigation › WorkTabs — several pieces of work open at once in one destination, each in its own tab.)
 *
 * A tab here is a whole task, not a view: a query, a record, an object, a request, a subscription.
 * That is the difference between this and its two siblings, and the reason it is a third component
 * rather than an option on either of them:
 *
 *  - `<caos-tab-strip>` shows the FACETS of one thing. Its tab set is authored and fixed; nobody
 *    opens a sixth facet of a record.
 *  - `<caos-workspace-tabs>` is the shell's app navigation. Its tabs are destinations, swapped
 *    wholesale when the app changes.
 *  - This one's tab set is made by the person using it, at runtime, and outlives nothing but the
 *    session. Tabs are opened, renamed by what they end up holding, and closed one at a time.
 *
 * ── Why this exists at all (CAOS-568) ──────────────────────────────────────────────────────────
 * The loudest complaint about the incumbent developer console is that it is one cramped window: the
 * moment two things are open, they are fighting over the same pane. The tool people install to
 * replace it did not answer that with a bigger window. It answered it by letting each destination
 * hold several pieces of work at once. A version of this application without that reproduces the
 * original complaint with a nicer paint job, so this is the component every destination that needs
 * it uses — one implementation, not a strip re-invented per screen, because the parts most likely
 * to be dropped as niceties are the ones below and they would be dropped at a different screen each
 * time.
 *
 * ── What it holds, rather than draws ───────────────────────────────────────────────────────────
 * THE NAME COMES FROM THE CONTENT. A tab starts as whatever opened it — "New Query 1" — and takes
 * the name of what it holds the moment it holds anything: "Account @10:38". A person with six tabs
 * open can then tell them apart without clicking, which is the entire point. The rule lives here:
 * a tab is given both names and this decides, so no caller can decide differently, and a content
 * name that goes away falls back rather than leaving a nameless tab.
 *
 * UNSAVED WORK IS MARKED, AND CLOSING ASKS. A dirty tab draws a mark and its close does not close:
 * it puts the question on screen and waits. The asking lives here for the same reason the naming
 * does — a destination that forgot it would silently discard somebody's work, and it would be a
 * different destination each time.
 *
 * HOW MANY IS A SETTING. `max` is an attribute with a documented default ({@link DEFAULT_MAX_WORK_TABS}),
 * never a number written into a screen. At the cap the "new" control is disabled and SAYS why,
 * rather than being absent or doing nothing when pressed.
 *
 * ── Data in ────────────────────────────────────────────────────────────────────────────────────
 *   `tabs` PROPERTY — {@link CaosWorkTab}[] (an array, so a property and not an attribute).
 *   `value` attribute — the id of the active tab.
 *   `max` attribute — how many this destination may hold.
 *   `new-label` attribute — the word on the control that opens another one.
 *
 * ── Events out (bubbling + composed; this component routes nothing and owns no work) ──────────
 *   `work-tab-select` — detail `{ id }`, a tab was chosen.
 *   `work-tab-new`    — the "new" control was used. Never fired at the cap.
 *   `work-tab-close`  — detail `{ id }`, a tab is to be closed. For a dirty tab this fires only
 *                       after the person has answered the question, so a destination can act on it
 *                       directly and never has to ask again.
 *
 * A11y: `role="tablist"` with a roving tabindex — only the active tab is tabbable, Arrow
 * Left/Right/Up/Down, Home and End move and activate — the WAI-ARIA tabs pattern its two siblings use. The close
 * mark inside a tab is a SPAN and not a button, deliberately: a button inside `role="tab"` is not a
 * valid child, and the keyboard route to closing is Delete or Backspace on the focused tab, which
 * is what the authoring practice for closable tabs prescribes.
 *
 * Styling seams: `::part(tablist)`, `::part(tab)`, `::part(close)`, `::part(new)`, `::part(ask)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';
import { makeIcon } from './icons.js';

/** One piece of open work. */
export interface CaosWorkTab {
  /** A stable id, made by whatever opened the tab. Carried on every event. */
  id: string;
  /**
   * The name the tab was OPENED with — "New Query 1", "Untitled request". Always present, because
   * a tab exists before it holds anything and a nameless tab is unusable in exactly the moment
   * somebody has several.
   */
  openedAs: string;
  /**
   * The name its CONTENT gives it, once it has content — "Account @10:38". Present ⇒ this is what
   * the tab shows. Absent, empty, or whitespace ⇒ it falls back to {@link CaosWorkTab.openedAs}
   * rather than drawing nothing: a tab whose content was cleared is a tab again, not a gap.
   */
  namedBy?: string;
  /** Unsaved work. Draws the mark, and makes closing ask instead of discard. */
  dirty?: boolean;
}

/**
 * How many pieces of work one destination holds when nothing says otherwise.
 *
 * Exported so a destination that genuinely needs a different number sets `max` against a named
 * default rather than against a number somebody has to go and find. Twelve because the strip stops
 * being readable well before a person stops being able to open tabs, and the failure of too low a
 * cap (a refusal you can see and argue with) is kinder than the failure of no cap (a strip of
 * twenty unreadable stubs).
 */
export const DEFAULT_MAX_WORK_TABS = 12;

let uid = 0;

const sheet = makeSheet(`
  :host { display: block; }

  .bar {
    display: flex;
    align-items: stretch;
    gap: var(--caos-space-2);
    border-bottom: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
  }

  .tablist {
    display: flex;
    align-items: stretch;
    gap: var(--caos-space-1);
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .tablist::-webkit-scrollbar { display: none; }

  .tab {
    appearance: none;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: var(--caos-color-text-muted);
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    /* The same mono micro-label voice the sibling strips use, so a developer surface does not read
       as a different product from the workspace around it. */
    font-family: var(--caos-font-mono);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1;
    padding: var(--caos-space-3) var(--caos-space-3);
    margin-bottom: -1px;
    cursor: pointer;
    white-space: nowrap;
    /* A long content name truncates rather than pushing every other tab off the strip — the point
       of the naming is telling tabs apart, which a strip scrolled out of view cannot do. */
    max-width: 22ch;
    transition: color 0.12s ease, border-color 0.12s ease;
  }
  .tab:hover:not([aria-selected="true"]) { color: var(--caos-color-text); }
  .tab[aria-selected="true"] {
    color: var(--caos-color-text);
    font-weight: 700;
    border-bottom-color: var(--caos-color-accent);
  }
  .tab:focus-visible {
    outline: none;
    border-radius: var(--caos-radius-sm);
    box-shadow: var(--caos-focus-ring);
  }

  .name { overflow: hidden; text-overflow: ellipsis; }

  /* The unsaved mark: a filled dot in the accent, ahead of the name where it reads before the
     word does. Not a colour ON the name — a tenant whose accent is pale would lose it entirely. */
  .dirty {
    flex: none;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--caos-color-accent);
  }

  .close {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: var(--caos-radius-sm);
    opacity: 0.55;
  }
  .close:hover { opacity: 1; background: var(--caos-color-bg); }
  .close svg { width: 10px; height: 10px; display: block; }

  .new {
    appearance: none;
    flex: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    font-family: var(--caos-font-mono);
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: var(--caos-space-3);
    cursor: pointer;
  }
  .new:hover:not(:disabled) { color: var(--caos-color-text); }
  .new:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }
  .new:focus-visible { outline: none; border-radius: var(--caos-radius-sm); box-shadow: var(--caos-focus-ring); }
  .new svg { width: 11px; height: 11px; display: block; }

  /* The question a dirty close asks. A row under the strip rather than a floating layer: it is
     about one tab in a bar that scrolls, and a popover pinned to a tab that scrolls away is a
     question with no visible subject. */
  .ask {
    display: flex;
    align-items: center;
    gap: var(--caos-space-3);
    padding: var(--caos-space-2) var(--caos-space-3);
    border-bottom: 1px solid var(--caos-color-border);
    /* The page ground rather than the strip's own white: the question is a different thing from
       the strip, and a row the same colour as the bar above it reads as part of the bar. */
    background: var(--caos-color-bg);
    font-size: 12px;
    color: var(--caos-color-text);
  }
  .ask[hidden] { display: none !important; }
  .ask-text { flex: 1 1 auto; min-width: 0; }
  .ask button {
    appearance: none;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: transparent;
    color: inherit;
    font: inherit;
    padding: 2px 10px;
    cursor: pointer;
  }
  .ask button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

export class CaosWorkTabs extends CaosElement {
  static get observedAttributes(): string[] {
    return ['value', 'max', 'new-label', 'aria-label'];
  }

  private bar: HTMLDivElement;
  private tablist: HTMLDivElement;
  private newBtn: HTMLButtonElement;
  private ask: HTMLDivElement;
  private askText: HTMLSpanElement;
  private askDiscard: HTMLButtonElement;
  private askKeep: HTMLButtonElement;

  private _tabs: CaosWorkTab[] = [];
  private buttons: HTMLButtonElement[] = [];
  /** The tab whose close is currently being asked about, or null. */
  private asking: string | null = null;
  private readonly seq = ++uid;

  constructor() {
    super(sheet);

    this.tablist = document.createElement('div');
    this.tablist.className = 'tablist';
    this.tablist.setAttribute('part', 'tablist');
    this.tablist.setAttribute('role', 'tablist');
    this.tablist.addEventListener('keydown', (e) => this.onKeydown(e));

    this.newBtn = document.createElement('button');
    this.newBtn.type = 'button';
    this.newBtn.className = 'new';
    this.newBtn.setAttribute('part', 'new');
    this.newBtn.append(makeIcon('plus'), document.createElement('span'));
    this.newBtn.addEventListener('click', () => {
      // Guarded here as well as by `disabled`, so the cap holds for a programmatic click too —
      // the cap is a rule about how many tabs may exist, not a look the control wears.
      if (this.atCap) return;
      this.dispatchEvent(new CustomEvent('work-tab-new', { bubbles: true, composed: true }));
    });

    this.bar = document.createElement('div');
    this.bar.className = 'bar';
    this.bar.append(this.tablist, this.newBtn);

    this.ask = document.createElement('div');
    this.ask.className = 'ask';
    this.ask.setAttribute('part', 'ask');
    this.ask.setAttribute('role', 'alertdialog');
    // Named by the sentence inside it, so a screen reader says WHICH tab has unsaved work rather
    // than announcing a nameless alert dialog. Deliberately NOT `aria-modal`, which the two other
    // dialogs in this library (overlay, inspector) do set: those cover the screen and trap Tab,
    // and this is a row under the strip that traps nothing. Claiming modality a component does not
    // enforce tells a screen reader the rest of the page is unreachable when it is still there.
    this.ask.setAttribute('aria-labelledby', `caos-work-ask-${this.seq}`);
    this.ask.hidden = true;
    this.askText = document.createElement('span');
    this.askText.className = 'ask-text';
    this.askText.id = `caos-work-ask-${this.seq}`;
    this.askDiscard = document.createElement('button');
    this.askDiscard.type = 'button';
    this.askDiscard.textContent = 'Close without saving';
    this.askKeep = document.createElement('button');
    this.askKeep.type = 'button';
    this.askKeep.textContent = 'Keep it open';
    this.ask.append(this.askText, this.askDiscard, this.askKeep);

    this.askDiscard.addEventListener('click', () => {
      const id = this.asking;
      this.closeAsk();
      if (id !== null) this.emitClose(id);
    });
    this.askKeep.addEventListener('click', () => {
      const id = this.asking;
      this.closeAsk();
      // Focus goes back to the tab that was nearly lost, not to nowhere.
      if (id !== null) this.buttons.find((b) => b.dataset['id'] === id)?.focus();
    });

    this.root.append(this.bar, this.ask);
  }

  connectedCallback(): void {
    this.syncName();
    this.render();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'aria-label') {
      this.syncName();
      return;
    }
    if (name === 'value') {
      this.reflectActive();
      return;
    }
    this.render();
  }

  private syncName(): void {
    applyAccessibleName(this.tablist, this.getAttribute('aria-label'), 'Open work');
  }

  /** The open work (data in). */
  get tabs(): CaosWorkTab[] {
    return this._tabs;
  }
  set tabs(list: CaosWorkTab[]) {
    this._tabs = Array.isArray(list) ? list : [];
    // A tab that has gone away takes its unanswered question with it. Leaving the question up would
    // put "you have unsaved work in X" on screen over a tab that no longer exists.
    if (this.asking !== null && !this._tabs.some((t) => t.id === this.asking)) this.closeAsk();
    this.render();
  }

  /** The active tab's id. */
  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(v: string) {
    this.setAttribute('value', v);
  }

  /**
   * How many tabs this destination may hold.
   *
   * A `max` that is not a positive whole number is read as "not set" and falls back to the default,
   * rather than being honoured as a cap of zero or of minus one — both of which would make the
   * destination permanently unusable through a typo in an attribute.
   */
  get max(): number {
    const raw = Number(this.getAttribute('max'));
    return Number.isInteger(raw) && raw > 0 ? raw : DEFAULT_MAX_WORK_TABS;
  }
  set max(n: number) {
    this.setAttribute('max', String(n));
  }

  /** Is this destination holding as much work as it may? */
  get atCap(): boolean {
    return this._tabs.length >= this.max;
  }

  /**
   * The name a tab SHOWS.
   *
   * The one rule this component exists to hold, and the one most likely to be dropped as a nicety:
   * what a tab holds names it, and what opened it is only the name it wears until then. Trimmed, so
   * a content name of spaces is no name at all rather than a blank tab.
   */
  static nameOf(tab: CaosWorkTab): string {
    const fromContent = (tab.namedBy ?? '').trim();
    return fromContent.length > 0 ? fromContent : tab.openedAs;
  }

  private render(): void {
    const newLabel = this.getAttribute('new-label') ?? 'New';
    (this.newBtn.lastElementChild as HTMLElement).textContent = newLabel;
    this.newBtn.disabled = this.atCap;
    // The cap SAYS so. A disabled control with no explanation is the dead end this application is
    // meant to be better than — and "why can I not open another one" has a real answer here.
    if (this.atCap) {
      this.newBtn.title = `This is as many as this can hold at once (${this.max}). Close one first.`;
    } else {
      this.newBtn.removeAttribute('title');
    }

    // Rebuilding the strip destroys the button that had focus. If focus was IN the strip — the
    // keyboard route to closing is Delete on a focused tab, and the destination then removes it —
    // it would land on nothing at all, which ends a keyboard session in the middle of the work.
    // Only when it was in the strip: taking focus from elsewhere on the page because a tab list
    // changed would be worse than losing it.
    const hadFocus = this.buttons.some((b) => b === this.root.activeElement);

    this.tablist.replaceChildren();
    this.buttons = this._tabs.map((tab, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tab';
      btn.setAttribute('part', 'tab');
      btn.setAttribute('role', 'tab');
      btn.id = `caos-work-tab-${this.seq}-${i}`;
      btn.dataset['id'] = tab.id;

      if (tab.dirty) {
        const mark = document.createElement('span');
        mark.className = 'dirty';
        // Named, not just drawn: a dot is invisible to anyone not looking at it.
        mark.setAttribute('aria-label', 'unsaved');
        btn.append(mark);
      }

      const name = document.createElement('span');
      name.className = 'name';
      name.textContent = CaosWorkTabs.nameOf(tab);
      btn.append(name);

      const close = document.createElement('span');
      close.className = 'close';
      close.setAttribute('part', 'close');
      close.append(makeIcon('close'));
      close.addEventListener('click', (e) => {
        // Closing is not selecting. Without this the tab is activated on its way out, which is a
        // visible flicker onto work that is about to disappear.
        e.stopPropagation();
        this.requestClose(tab.id);
      });
      btn.append(close);

      btn.addEventListener('click', () => this.select(tab.id, true));
      this.tablist.append(btn);
      return btn;
    });

    // Nothing active, or an active id that is no longer here ⇒ fall to the first tab. A strip whose
    // `value` names a closed tab draws no selection at all, which reads as "nothing is open".
    const active = this.getAttribute('value');
    if (this._tabs.length > 0 && !this._tabs.some((t) => t.id === active)) {
      this.setAttribute('value', this._tabs[0]!.id);
      this.restoreFocus(hadFocus);
      return; // the attribute change re-enters through reflectActive
    }
    this.reflectActive();
    this.restoreFocus(hadFocus);
  }

  /**
   * Put focus back on the strip after a rebuild that destroyed the element holding it.
   *
   * It goes to the ACTIVE tab, which after a close is the one the destination moved to — so the
   * next arrow key carries on from where the work now is. With no tabs left there is nothing to
   * focus and nothing to do.
   */
  private restoreFocus(hadFocus: boolean): void {
    if (!hadFocus || this.buttons.length === 0) return;
    if (this.buttons.some((b) => b === this.root.activeElement)) return;
    const active = this.getAttribute('value');
    (this.buttons.find((b) => b.dataset['id'] === active) ?? this.buttons[0])!.focus();
  }

  private reflectActive(): void {
    const active = this.getAttribute('value');
    this.buttons.forEach((btn) => {
      const isActive = btn.dataset['id'] === active;
      btn.setAttribute('aria-selected', String(isActive));
      btn.tabIndex = isActive ? 0 : -1;
    });
  }

  private select(id: string, focus: boolean): void {
    if (this.getAttribute('value') !== id) {
      this.setAttribute('value', id);
      this.dispatchEvent(new CustomEvent('work-tab-select', { bubbles: true, composed: true, detail: { id } }));
    }
    if (focus) this.buttons.find((b) => b.dataset['id'] === id)?.focus();
  }

  /**
   * Close a tab — or ask first.
   *
   * A clean tab closes. A dirty one puts the question on screen and emits NOTHING until it is
   * answered, so a destination listening for `work-tab-close` can act on it directly: by the time
   * it arrives, somebody has said yes.
   */
  private requestClose(id: string): void {
    const tab = this._tabs.find((t) => t.id === id);
    if (!tab) return;
    // A question already up about a DIFFERENT tab is replaced, not queued. Nothing is lost by
    // that: an unanswered question closes nothing, so the first tab stays open with its work in
    // it. Queueing would ask about a tab the person has since moved on from, and refusing would
    // leave a close mark that does nothing.
    if (!tab.dirty) {
      this.emitClose(id);
      return;
    }
    this.asking = id;
    this.askText.textContent = `"${CaosWorkTabs.nameOf(tab)}" has unsaved work.`;
    this.ask.hidden = false;
    // Focus lands on KEEPING it. The question exists because the work is worth a question, and the
    // key that raised it — Delete, Backspace, Enter on a close mark — is one a hand can still be
    // on; a default of "close without saving" turns a second keystroke into the loss this asks
    // about. The safe answer is the default answer, and discarding is the one you have to reach for.
    this.askKeep.focus();
  }

  private closeAsk(): void {
    this.asking = null;
    this.ask.hidden = true;
    this.askText.textContent = '';
  }

  private emitClose(id: string): void {
    this.dispatchEvent(new CustomEvent('work-tab-close', { bubbles: true, composed: true, detail: { id } }));
  }

  private onKeydown(e: KeyboardEvent): void {
    // Delete/Backspace on the focused tab is the keyboard route to closing, because the close mark
    // is not focusable — see the header note on why it is a span and not a button.
    if (e.key === 'Delete' || e.key === 'Backspace') {
      const focused = this.buttons.find((b) => b === this.root.activeElement);
      const id = focused?.dataset['id'];
      if (id !== undefined) {
        e.preventDefault();
        this.requestClose(id);
      }
      return;
    }

    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key) || this.buttons.length === 0) return;
    e.preventDefault();
    const current = this.buttons.find((b) => b.dataset['id'] === this.getAttribute('value'));
    let idx = current ? this.buttons.indexOf(current) : 0;
    if (idx < 0) idx = 0;
    const last = this.buttons.length - 1;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        idx = idx >= last ? 0 : idx + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        idx = idx <= 0 ? last : idx - 1;
        break;
      case 'Home':
        idx = 0;
        break;
      default:
        idx = last;
        break;
    }
    const target = this.buttons[idx];
    if (target?.dataset['id'] !== undefined) this.select(target.dataset['id'], true);
  }
}

define('caos-work-tabs', CaosWorkTabs);

declare global {
  interface HTMLElementTagNameMap {
    'caos-work-tabs': CaosWorkTabs;
  }
}
